import { IconButton, Chip } from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { createUseStyles } from "$utils/createUseStyles";
import { theme } from "$utils/theme/bataillog";

import { QUANTITY_BADGE_WIDTH } from "$hooks/useEDBAvailableOrders";
import { Badge, Text } from "$components";
import { TreeProduct } from "./types";
import QuantityPicker from "../QuantityPicker/QuantityPicker";

type Props = {
  isCheckable?: boolean;
  largerVariant?: boolean;
  onQuantityChange?: (quantity: number, product: TreeProduct) => void;
  onProductDelete?: (treeProduct: TreeProduct) => void;
  treeProduct: TreeProduct;
};

function ProductLabel({
  isCheckable,
  largerVariant = false,
  onQuantityChange,
  onProductDelete,
  treeProduct,
}: Props) {
  const { order, product, suborder } = treeProduct;
  const {
    reference,
    name,
    quantity,
    unit,
    integrableQuantity,
    initialQuantity,
  } = product;
  const isAnIntegrableOrder = isCheckable;
  const isQuantityEditable = !!onQuantityChange;
  const classes = useStyles({
    isQuantityEditable,
    isAnIntegrableOrder,
    largerVariant,
  });
  const { format: formatNb } = new Intl.NumberFormat("fr-FR");

  const handleOnDelete = () => {
    onProductDelete({ order, product, suborder } as TreeProduct);
  };

  const handleOnQuantityChange = (quantity: number) => {
    onQuantityChange(quantity, treeProduct);
  };

  const badgeColor =
    integrableQuantity === quantity
      ? "green"
      : integrableQuantity === 0
        ? "red"
        : "orange";

  const maxValue = initialQuantity
    ? integrableQuantity + initialQuantity
    : integrableQuantity;

  return (
    <div className={classes.wrapper}>
      {onProductDelete && (
        <IconButton
          size="small"
          aria-label="delete product"
          onClick={handleOnDelete}
        >
          <ClearRoundedIcon />
        </IconButton>
      )}
      <div className={classes.productInfo}>
        <Text className={classes.reference}>
          <b>{reference}</b>
        </Text>
        <Text className={classes.designation}>{name}</Text>
      </div>

      {!isQuantityEditable && !isAnIntegrableOrder && (
        <div className={classes.qtyAndUnit}>
          <Chip
            size="small"
            label={formatNb(quantity)}
            className={classes.qty}
          />
          <Text type="secondary" className={classes.unit}>
            ({unit?.toUpperCase()})
          </Text>
        </div>
      )}

      {isAnIntegrableOrder && (
        <>
          <Badge
            color={badgeColor}
            className={classes.quantityBadge}
          >{`${formatNb(integrableQuantity)} / ${formatNb(quantity)}`}</Badge>
          <Text type="secondary" style={{ margin: 0 }}>
            ({unit?.toUpperCase()})
          </Text>
        </>
      )}

      {isQuantityEditable && (
        <div className={classes.quantityWrapper}>
          <QuantityPicker
            className={classes.quantityBadge}
            disabled={!integrableQuantity && integrableQuantity !== 0}
            maxValue={maxValue}
            minValue={1}
            product={product}
            onQuantityChange={handleOnQuantityChange}
            quantity={quantity}
          />
          <Text type="secondary">({unit?.toUpperCase()})</Text>
        </div>
      )}
    </div>
  );
}

export default ProductLabel;

const qtyAndUnitWidth = "140px";

const useStyles = createUseStyles(() => ({
  ellipsedText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  productInfo: {
    alignItems: "center",
    display: "flex",
    width: (props: {
      isQuantityEditable: boolean;
      isAnIntegrableOrder: boolean;
      largerVariant: boolean;
    }) =>
      props.isQuantityEditable || props.isAnIntegrableOrder
        ? `${420 + (props.largerVariant ? 32 : 0)}px`
        : "",
    flex: (props: {
      isQuantityEditable: boolean;
      isAnIntegrableOrder: boolean;
      largerVariant: boolean;
    }) => (!props.isQuantityEditable && !props.isAnIntegrableOrder ? 1 : ""),
    overflow: "hidden",
  },
  reference: {
    composes: "$ellipsedText",
    fontSize: "90%",
    minWidth: "100px",
  },
  designation: {
    composes: "$ellipsedText",
    flex: "0 1 auto",
  },
  quantityPicker: {
    position: "relative",
  },
  quantityWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
  },
  wrapper: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
  },
  qtyAndUnit: {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    width: qtyAndUnitWidth,
  },
  qty: {
    margin: `0 ${theme.spacing(1)}px 0 0`,
    width: "60px", // approx. 5 digits
  },
  unit: {
    composes: "$ellipsedText",
    margin: 0,
    flex: 1,
  },
  quantityBadge: {
    width: QUANTITY_BADGE_WIDTH,
  },
}));
